import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Calendar, Clock, Layers, User, StickyNote, Sparkles, ChevronLeft, ChevronRight, Grid3X3, Orbit, GitBranch, AlignCenter, CheckCircle, Play, Eye, EyeOff, Maximize2, Minimize2 } from 'lucide-react';
import { getMyTeams } from '../../api/teamApi';
import './ConstellationTimeline.css';
import './constellation-engine.css';

// ── Universe palette (theme-blended) ──
const NEBULA_COLORS = [
  '#6366f1', '#8b5cf6', '#a855f7', '#ec4899',
  '#06b6d4', '#14b8a6', '#f59e0b', '#3b82f6',
  '#ef4444', '#10b981', '#f97316',
  '#1B1464', '#2D2B8F', '#C9A227',
];

const FINISHED_COLOR = '#10b981'; // Emerald green for finished tasks
const IN_PROCESS_COLOR = '#22d3ee'; // Soft cyan glow for ongoing tasks

const isFinishedEntry = (entry) => {
  return entry.processingStatusCode === 'Finished' || entry.processingStatusCode === '3' ||
    entry.activityProcessingStatusCode === 'Finished' || entry.activityProcessingStatusCode === '3';
};

const isInProcessEntry = (entry) => {
  return entry.isRunning || entry.processingStatusCode === 'In Process' || entry.processingStatusCode === '2' ||
    entry.processingStatusCode === 'IN_PROGRESS' || entry.activityProcessingStatusCode === 'In Process' ||
    entry.activityProcessingStatusCode === '2' || entry.activityProcessingStatusCode === 'IN_PROGRESS';
};

const colorAt = (i, isFinished = false) => {
  if (isFinished) return FINISHED_COLOR;
  return NEBULA_COLORS[i % NEBULA_COLORS.length];
};

const MONTH_OPTIONS = [
  { value: 'all', label: 'All Months', short: 'All' },
  { value: '0', label: 'January', short: 'Jan' },
  { value: '1', label: 'February', short: 'Feb' },
  { value: '2', label: 'March', short: 'Mar' },
  { value: '3', label: 'April', short: 'Apr' },
  { value: '4', label: 'May', short: 'May' },
  { value: '5', label: 'June', short: 'Jun' },
  { value: '6', label: 'July', short: 'Jul' },
  { value: '7', label: 'August', short: 'Aug' },
  { value: '8', label: 'September', short: 'Sep' },
  { value: '9', label: 'October', short: 'Oct' },
  { value: '10', label: 'November', short: 'Nov' },
  { value: '11', label: 'December', short: 'Dec' },
];

const pad2 = (value) => String(value).padStart(2, '0');

const monthRangeFor = (year, month) => {
  const numericYear = Number(year);
  const numericMonth = Number(month);
  const start = new Date(numericYear, numericMonth, 1);
  const end = new Date(numericYear, numericMonth + 1, 0);
  return {
    from: `${start.getFullYear()}-${pad2(start.getMonth() + 1)}-01`,
    to: `${end.getFullYear()}-${pad2(end.getMonth() + 1)}-${pad2(end.getDate())}`,
  };
};

const yearRangeFor = (year) => ({ from: `${year}-01-01`, to: `${year}-12-31` });

// ── Space-themed nebula names sprinkled on a few entries ──
const NEBULA_NAMES = [
  'Milky Way', 'Andromeda Nebula', 'Orion Cluster', 'Pleiades Veil',
  'Helix Nebula', 'Cassiopeia Drift', 'Sombrero Galaxy', 'Crab Nebula',
  'Eagle Nebula', 'Tarantula Cloud', 'Carina Veil', 'Lyra Spiral',
  'Vega Cluster', 'Sirius Stream', 'Triangulum Mist',
];

const SHOOTING_STARS = Array.from({ length: 14 }, (_, i) => ({
  top: `${4 + (i % 7) * 6}%`,
  delay: `${i * 1.7}s`,
  duration: `${7 + (i % 5) * 1.2}s`,
  length: `${72 + (i % 4) * 24}px`,
  opacity: 0.24 + (i % 4) * 0.08,
}));

/** Deterministic pseudo-random 0-1 from a string seed */
const seedRandom = (seed) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  h = ((h >>> 16) ^ h) * 0x45d9f3b;
  h = ((h >>> 16) ^ h) * 0x45d9f3b;
  h = (h >>> 16) ^ h;
  return (h & 0x7fffffff) / 0x7fffffff;
};

// ── View Templates ──
const VIEW_TEMPLATES = [
  {
    id: 'scatter',
    name: 'Star Scatter',
    icon: Grid3X3,
    desc: 'Random constellation scatter',
    nodeSpacing: 64,
    dateGap: 32,
    xRange: [10, 90],
    xFn: (id, idx) => {
      const r = seedRandom(String(id || '') + idx);
      return 10 + r * 80;
    },
  },
  {
    id: 'spiral',
    name: 'Spiral Galaxy',
    icon: Orbit,
    desc: 'Nodes spiral outward from center',
    nodeSpacing: 70,
    dateGap: 36,
    xRange: [10, 90],
    xFn: (id, idx) => {
      // True Archimedean spiral — nodes wind outward from center
      const goldenAngle = 2.39996; // ~137.5 degrees in radians
      const angle = idx * goldenAngle;
      const maxRadius = 38;
      const radius = Math.min(maxRadius, 6 + Math.sqrt(idx) * 9);
      const x = 50 + Math.cos(angle) * radius;
      return Math.max(10, Math.min(90, x));
    },
  },
  {
    id: 'zigzag',
    name: 'Nebula Stream',
    icon: GitBranch,
    desc: 'Flowing left-right wave pattern',
    nodeSpacing: 60,
    dateGap: 30,
    xRange: [15, 85],
    xFn: (id, idx) => {
      const wave = Math.sin(idx * 0.7) * 32;
      const drift = seedRandom(String(id || '') + idx) * 10 - 5;
      return 50 + wave + drift;
    },
  },
  {
    id: 'linear',
    name: 'Light Stream',
    icon: AlignCenter,
    desc: 'Centered linear flow',
    nodeSpacing: 52,
    dateGap: 24,
    xRange: [35, 65],
    xFn: (id, idx) => {
      const jitter = seedRandom(String(id || '') + idx) * 24 - 12;
      return 50 + jitter;
    },
  },
];

// ── Format helpers ──
const fmtDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = dateStr.slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const yest = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (d === today) return 'Today';
  if (d === yest) return 'Yesterday';
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const fmtTime = (t) => {
  if (!t) return '';
  const parts = t.split(':');
  if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
  return t;
};

const fmtHours = (entry) => {
  const h = parseFloat(entry.hours) || 0;
  if (h === 0 && entry.isRunning) return 'Running…';
  if (h === 0) return '0h';
  if (h < 1) return `${Math.round(h * 60)}m`;
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return mins === 0 ? `${hrs}h` : `${hrs}h ${mins}m`;
};

const fmtFullDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr.slice(0, 10)).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
};

const defaultDateFrom = () => {
  const now = new Date();
  return monthRangeFor(now.getFullYear(), now.getMonth()).from;
};
const defaultDateTo = () => {
  const now = new Date();
  return monthRangeFor(now.getFullYear(), now.getMonth()).to;
};

// ── Nebula fog config ──
const NEBULA_FOGS = [
  { top: '8%', left: '5%', w: 320, h: 220, bg: 'rgba(99,102,241,0.12)' },
  { top: '30%', left: '65%', w: 260, h: 200, bg: 'rgba(168,85,247,0.10)' },
  { top: '55%', left: '15%', w: 300, h: 180, bg: 'rgba(201,162,39,0.08)' },
  { top: '75%', left: '55%', w: 280, h: 240, bg: 'rgba(6,182,212,0.09)' },
];

// ── Claude design: deep-space starfield (parallax) + nebula clouds ──
const STARS_FAR = Array.from({ length: 140 }, (_, i) => ({
  top: seedRandom(`sf-t-${i}`) * 100,
  left: seedRandom(`sf-l-${i}`) * 100,
  size: 0.6 + seedRandom(`sf-s-${i}`) * 1.1,
  opacity: 0.25 + seedRandom(`sf-o-${i}`) * 0.5,
  twinkle: 2 + seedRandom(`sf-tw-${i}`) * 4,
  delay: seedRandom(`sf-d-${i}`) * 5,
}));
const STARS_NEAR = Array.from({ length: 60 }, (_, i) => ({
  top: seedRandom(`sn-t-${i}`) * 100,
  left: seedRandom(`sn-l-${i}`) * 100,
  size: 1.2 + seedRandom(`sn-s-${i}`) * 1.8,
  opacity: 0.5 + seedRandom(`sn-o-${i}`) * 0.5,
  twinkle: 1.5 + seedRandom(`sn-tw-${i}`) * 3,
  delay: seedRandom(`sn-d-${i}`) * 4,
}));
const NEBULA_CLOUDS_DESIGN = [
  { top: 8, left: 12, w: 38, h: 30, bg: 'radial-gradient(circle, rgba(99,102,241,0.32) 0%, rgba(99,102,241,0) 70%)' },
  { top: 32, left: 64, w: 30, h: 26, bg: 'radial-gradient(circle, rgba(139,92,246,0.26) 0%, rgba(139,92,246,0) 72%)' },
  { top: 58, left: 20, w: 34, h: 28, bg: 'radial-gradient(circle, rgba(34,211,238,0.22) 0%, rgba(34,211,238,0) 70%)' },
  { top: 75, left: 60, w: 36, h: 30, bg: 'radial-gradient(circle, rgba(236,72,153,0.18) 0%, rgba(236,72,153,0) 72%)' },
  { top: 18, left: 78, w: 22, h: 20, bg: 'radial-gradient(circle, rgba(139,92,246,0.26) 0%, rgba(139,92,246,0) 72%)' },
];

// Star-map "RA / Dec" coordinate label (deterministic from date key)
const starCoord = (dateKey, idx = 0) => {
  const r = seedRandom(dateKey + 'ra');
  const d = seedRandom(dateKey + 'dec');
  const ra = Math.floor(r * 24);
  const ram = Math.floor(seedRandom(dateKey + 'ram') * 60);
  const dec = Math.floor(d * 90);
  const sign = idx % 2 === 0 ? '+' : '\u2212';
  return `RA ${String(ra).padStart(2, '0')}h ${String(ram).padStart(2, '0')}m \u00b7 Dec ${sign}${String(dec).padStart(2, '0')}\u00b0`;
};

const DATE_HEADER_HEIGHT = 48;

// ──────────────────────────────────────────────
//  Build curved SVG path between two points
// ──────────────────────────────────────────────
const curvedPath = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  // Control points for a nice S-curve
  const cx1 = x1 + dx * 0.15;
  const cy1 = y1 + dy * 0.5;
  const cx2 = x2 - dx * 0.15;
  const cy2 = y1 + dy * 0.5;
  return `M ${x1} ${y1} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${x2} ${y2}`;
};

// ──────────────────────────────────────────────
//  Constellation Timeline Component
// ──────────────────────────────────────────────
const ConstellationTimeline = ({ entries = [], elapsedMap = {}, isLoadingMore = false, hasMore: hasMoreEntries = false, onLoadMore, onContinueEntry }) => {
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth()));
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [hoveredId, setHoveredId] = useState(null);
  const [pinnedId, setPinnedId] = useState(null);
  const [teamLead, setTeamLead] = useState(null);
  const [template, setTemplate] = useState('scatter');
  const [activeDay, setActiveDay] = useState(null);
  const [renderWindow, setRenderWindow] = useState({ top: 0, bottom: 1800 });
  const [showFilters, setShowFilters] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(40);
  const loadSentinelRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const dayCardsRef = useRef(null);
  const dayRefs = useRef({});
  const loadMoreSentinelRef = useRef(null);

  // Detect mobile for tighter spacing
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const activeTemplate = VIEW_TEMPLATES.find(t => t.id === template) || VIEW_TEMPLATES[0];

  // active card: pinned takes priority over hover
  const activeId = pinnedId || hoveredId;

  const availableYears = useMemo(() => {
    const years = new Set([String(new Date().getFullYear())]);
    entries.forEach((entry) => {
      const year = (entry.date || '').slice(0, 4);
      if (year) years.add(year);
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [entries]);

  useEffect(() => {
    if (!selectedYear) return;
    const range = selectedMonth === 'all'
      ? yearRangeFor(selectedYear)
      : monthRangeFor(selectedYear, selectedMonth);
    setDateFrom(range.from);
    setDateTo(range.to);
    setActiveDay(null);
    setPinnedId(null);
    setVisibleCount(40);
  }, [selectedMonth, selectedYear]);

  // Reset progressive load when template changes (so re-construction restarts)
  useEffect(() => {
    setVisibleCount(40);
  }, [template]);

  // ── Fullscreen toggle ──
  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const fullscreenEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (!fullscreenEl) {
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (req) req.call(el);
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
      if (exit) exit.call(document);
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const fullscreenEl = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(Boolean(fullscreenEl));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  // ── Progressive load on scroll: when sentinel is near viewport, grow visibleCount ──
  useEffect(() => {
    const node = loadSentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entriesObs) => {
        for (const e of entriesObs) {
          if (e.isIntersecting) {
            setVisibleCount((c) => c + 30);
          }
        }
      },
      { root: null, rootMargin: '300px 0px 300px 0px', threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await getMyTeams();
        const teams = res?.data ?? res ?? [];
        if (teams.length > 0) {
          setTeamLead({
            name: teams[0].leadName || 'Unknown',
            teamName: teams[0].name || teams[0].teamName || '',
            department: teams[0].department || '',
          });
        }
      } catch { /* silently fall back */ }
    })();
  }, []);

  // Track previous visible count so progressive batches animate from 0 delay
  const prevVisibleRef = useRef(0);
  useEffect(() => {
    prevVisibleRef.current = visibleCount;
  }, [visibleCount, template, dateFrom, dateTo]);

  // ── Filter & sort ──
  const filtered = useMemo(() => {
    return entries
      .filter(e => {
        const d = (e.date || '').slice(0, 10);
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
      })
      .sort((a, b) => {
        const da = (a.date || '').slice(0, 10);
        const db = (b.date || '').slice(0, 10);
        if (da !== db) return db.localeCompare(da);
        return (b.startTime || '').localeCompare(a.startTime || '');
      });
  }, [entries, dateFrom, dateTo]);

  // Progressive slice: render only first N entries; grow as user scrolls
  const visibleFiltered = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );

  const hasMore = visibleCount < filtered.length;

  const grouped = useMemo(() => {
    const map = {};
    visibleFiltered.forEach(e => {
      const d = (e.date || '').slice(0, 10);
      if (!map[d]) map[d] = [];
      map[d].push(e);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [visibleFiltered]);

  const yearEntries = useMemo(() => {
    return entries.filter((entry) => (entry.date || '').slice(0, 4) === selectedYear);
  }, [entries, selectedYear]);

  const monthlyResults = useMemo(() => {
    return MONTH_OPTIONS.filter((month) => month.value !== 'all').map((month) => {
      const monthEntries = yearEntries.filter((entry) => {
        const d = new Date((entry.date || '').slice(0, 10));
        return !Number.isNaN(d.getTime()) && String(d.getMonth()) === month.value;
      });
      const hours = monthEntries.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0);
      return {
        ...month,
        entries: monthEntries.length,
        hours,
        completed: monthEntries.filter(isFinishedEntry).length,
        ongoing: monthEntries.filter((entry) => !entry.isRunning && !isFinishedEntry(entry) && isInProcessEntry(entry)).length,
        running: monthEntries.filter((entry) => entry.isRunning).length,
      };
    });
  }, [yearEntries]);

  const periodStats = useMemo(() => {
    return {
      entries: filtered.length,
      hours: filtered.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0),
      completed: filtered.filter(isFinishedEntry).length,
      ongoing: filtered.filter((entry) => !entry.isRunning && !isFinishedEntry(entry) && isInProcessEntry(entry)).length,
      running: filtered.filter((entry) => entry.isRunning).length,
    };
  }, [filtered]);

  const totalHours = useMemo(() => {
    return filtered.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
  }, [filtered]);

  const completedCount = useMemo(() => {
    return filtered.filter(isFinishedEntry).length;
  }, [filtered]);

  // ── Pick 4 deterministic random entries to label as named nebulae ──
  const nebulaPicks = useMemo(() => {
    if (filtered.length === 0) return {};
    const ranked = filtered
      .map((entry) => ({ id: entry.id, r: seedRandom(String(entry.id) + 'nebula') }))
      .sort((a, b) => a.r - b.r)
      .slice(0, Math.min(4, filtered.length));
    const picks = {};
    ranked.forEach((p) => {
      const idx = Math.floor(seedRandom(String(p.id) + 'name') * NEBULA_NAMES.length);
      picks[p.id] = NEBULA_NAMES[idx];
    });
    return picks;
  }, [filtered]);

  // ── Compute scatter positions for all nodes (and date headers) ──
  const { positions, datePositions, totalHeight } = useMemo(() => {
    const pos = {};
    const datePos = {};
    let y = 20;
    const spacing = isMobile ? Math.round(activeTemplate.nodeSpacing * 0.5) : activeTemplate.nodeSpacing;
    const gap = isMobile ? Math.round(activeTemplate.dateGap * 0.45) : activeTemplate.dateGap;

    grouped.forEach(([dateKey, dayEntries]) => {
      datePos[dateKey] = y;
      y += DATE_HEADER_HEIGHT + 12;

      dayEntries.forEach((entry, i) => {
        const globalIdx = filtered.indexOf(entry);
        const isFinished = isFinishedEntry(entry);
        let x = activeTemplate.xFn(entry.id, globalIdx);
        // On mobile, tighten the x-range to prevent nodes from being too close to edges
        if (isMobile) {
          x = Math.max(15, Math.min(85, x));
        }
        pos[entry.id] = { x, y, color: colorAt(globalIdx, isFinished), globalIdx, isFinished };
        y += spacing;
      });

      y += gap;
    });

    return { positions: pos, datePositions: datePos, totalHeight: y + 20 };
  }, [grouped, filtered, activeTemplate, isMobile]);

  // ── Build curved connector segments ──
  const connectors = useMemo(() => {
    const lines = [];
    const flat = grouped.flatMap(([, items]) => items);
    for (let i = 0; i < flat.length - 1; i++) {
      const a = flat[i];
      const b = flat[i + 1];
      const pa = positions[a.id];
      const pb = positions[b.id];
      if (!pa || !pb) continue;

      // Check if either entry is finished to determine connector color
      const isAFinished = isFinishedEntry(a);
      const isBFinished = isFinishedEntry(b);
      const isAInProcess = isInProcessEntry(a);
      const isBInProcess = isInProcessEntry(b);

      lines.push({
        id: `${a.id}-${b.id}`,
        path: curvedPath(pa.x, pa.y, pb.x, pb.y),
        colorA: pa.color,
        colorB: pb.color,
        aId: a.id,
        bId: b.id,
        isFinishedSegment: isAFinished || isBFinished,
        isInProcessSegment: !isAFinished && !isBFinished && (isAInProcess || isBInProcess),
      });
    }
    return lines;
  }, [grouped, positions]);

  // ── Day summaries for navigation cards ──
  const daySummaries = useMemo(() => {
    return grouped.map(([dateKey, dayEntries], groupIdx) => {
      const hrs = dayEntries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
      const hasRunning = dayEntries.some(e => e.isRunning);
      const hasFinished = dayEntries.some(isFinishedEntry);
      const hasInProcess = dayEntries.some(e => !e.isRunning && !isFinishedEntry(e) && isInProcessEntry(e));
      const color = colorAt(groupIdx * 3); // distinct color per day
      return { dateKey, count: dayEntries.length, hours: hrs, hasRunning, hasFinished, hasInProcess, color };
    });
  }, [grouped]);

  const resetFilters = () => {
    setSelectedMonth(String(new Date().getMonth()));
    setSelectedYear(String(new Date().getFullYear()));
  };

  // ── Scroll to day ──
  const scrollToDay = useCallback((dateKey) => {
    setActiveDay(dateKey);
    const ref = dayRefs.current[dateKey];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (canvasRef.current && typeof window !== 'undefined') {
      const y = datePositions[dateKey];
      if (typeof y === 'number') {
        const rect = canvasRef.current.getBoundingClientRect();
        const absoluteTop = window.scrollY + rect.top;
        window.scrollTo({ top: Math.max(0, absoluteTop + y - 120), behavior: 'smooth' });
      }
    }
  }, [datePositions]);

  // ── Day card strip scroll helpers ──
  const scrollDayCards = (dir) => {
    if (!dayCardsRef.current) return;
    dayCardsRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  const updateRenderWindow = useCallback(() => {
    if (!canvasRef.current || typeof window === 'undefined') return;
    const rect = canvasRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 900;
    const topInsideCanvas = Math.max(0, -rect.top);
    const bottomInsideCanvas = topInsideCanvas + viewportHeight;
    setRenderWindow({
      top: Math.max(0, topInsideCanvas - 700),
      bottom: bottomInsideCanvas + 1000,
    });
  }, []);

  useEffect(() => {
    updateRenderWindow();
    if (typeof window === 'undefined') return undefined;

    window.addEventListener('scroll', updateRenderWindow, { passive: true });
    window.addEventListener('resize', updateRenderWindow);

    return () => {
      window.removeEventListener('scroll', updateRenderWindow);
      window.removeEventListener('resize', updateRenderWindow);
    };
  }, [updateRenderWindow, totalHeight, filtered.length, template]);

  useEffect(() => {
    if (!onLoadMore || !hasMoreEntries || isLoadingMore || !loadMoreSentinelRef.current || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entriesToObserve) => {
        if (entriesToObserve[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      { root: null, rootMargin: '320px 0px 320px 0px', threshold: 0.01 }
    );

    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [onLoadMore, hasMoreEntries, isLoadingMore, filtered.length]);

  const renderedGrouped = useMemo(() => {
    return grouped.filter(([dateKey, dayEntries]) => {
      const headerY = datePositions[dateKey] || 0;
      const lastY = dayEntries.reduce((maxY, entry) => Math.max(maxY, positions[entry.id]?.y || headerY), headerY) + activeTemplate.nodeSpacing;
      return lastY >= renderWindow.top && headerY <= renderWindow.bottom;
    });
  }, [grouped, datePositions, positions, renderWindow, activeTemplate]);

  const renderedConnectors = useMemo(() => {
    return connectors.filter((connector) => {
      const aY = positions[connector.aId]?.y ?? -1;
      const bY = positions[connector.bId]?.y ?? -1;
      const minY = Math.min(aY, bY);
      const maxY = Math.max(aY, bY);
      return maxY >= renderWindow.top && minY <= renderWindow.bottom;
    });
  }, [connectors, positions, renderWindow]);

  const renderedEntries = useMemo(() => {
    return filtered.filter((entry) => {
      const y = positions[entry.id]?.y;
      return typeof y === 'number' && y >= renderWindow.top && y <= renderWindow.bottom;
    });
  }, [filtered, positions, renderWindow]);

  // ── Render ──
  return (
    <div className={`constellation-timeline nebula-border rounded-2xl max-w-full ${isFullscreen ? 'is-fullscreen overflow-y-auto overflow-x-hidden' : 'overflow-hidden'}`} ref={containerRef}>
      {/* ── Cosmos background: gradient + parallax starfield + drifting nebula clouds ── */}
      <div className="ce-bg-grad rounded-2xl" aria-hidden="true" />
      <div className="ce-nebula-layer" aria-hidden="true">
        {NEBULA_CLOUDS_DESIGN.map((c, i) => (
          <div
            key={`neb-${i}`}
            className="ce-nebula-cloud"
            style={{
              top: `${c.top}%`,
              left: `${c.left}%`,
              width: `${c.w}%`,
              height: `${c.h}%`,
              background: c.bg,
              animationDelay: `${i * 1.5}s`,
              animationDuration: `${18 + i * 3}s`,
            }}
          />
        ))}
      </div>
      <div className="ce-starfield" aria-hidden="true">
        {STARS_FAR.map((s, i) => (
          <span
            key={`sf-${i}`}
            className="ce-star ce-star-far"
            style={{
              top: `${s.top}%`,
              left: `${s.left}%`,
              width: s.size,
              height: s.size,
              opacity: s.opacity,
              animationDuration: `${s.twinkle}s`,
              animationDelay: `${s.delay}s`,
            }}
          />
        ))}
        {STARS_NEAR.map((s, i) => (
          <span
            key={`sn-${i}`}
            className="ce-star ce-star-near"
            style={{
              top: `${s.top}%`,
              left: `${s.left}%`,
              width: s.size,
              height: s.size,
              opacity: s.opacity,
              animationDuration: `${s.twinkle}s`,
              animationDelay: `${s.delay}s`,
              boxShadow: `0 0 ${s.size * 4}px rgba(255,255,255,0.5)`,
            }}
          />
        ))}
      </div>
      {/* Legacy shooting stars retained for subtle motion */}
      <div className="shooting-star-field" aria-hidden="true">
        {SHOOTING_STARS.map((star, i) => (
          <span
            key={i}
            className="shooting-star"
            style={{
              '--star-top': star.top,
              '--star-delay': star.delay,
              '--star-duration': star.duration,
              '--star-length': star.length,
              '--star-opacity': star.opacity,
            }}
          />
        ))}
      </div>

      {/* Content layer */}
      <div className="constellation-content-layer relative z-10 p-1.5 sm:p-6">
        {/* ── Controls bar (sticky on desktop, scrolls on mobile) ── */}
        <div className="sticky-controls-bar sm:sticky sm:top-16 z-40 -mx-1.5 sm:-mx-6 px-1.5 sm:px-6 pt-1 sm:pt-4 pb-0.5 sm:pb-2" style={{ background: 'linear-gradient(to bottom, rgba(5,5,22,0.97) 0%, rgba(5,5,22,0.92) 70%, rgba(5,5,22,0) 100%)' }}>

          <div className={`filters-collapsible ${showFilters ? 'is-open' : 'is-closed'}`}>

            {/* ── Header (compact on mobile) ── */}
            <div className="flex items-center justify-between gap-2 mb-1 sm:mb-3">
              <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
                <div className="hidden sm:block p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/30">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xs sm:text-lg font-bold text-white truncate">Constellation Timeline</h2>
                  <p className="text-[8px] sm:text-[11px] text-indigo-300/70 truncate">
                    {filtered.length} entries · {Math.round(totalHours * 10) / 10}h
                    {completedCount > 0 && (
                      <span className="ml-1 sm:ml-2 inline-flex items-center gap-0.5 sm:gap-1 px-1 sm:px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                        <CheckCircle className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                        {completedCount}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Month / year filters */}
              <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                <select
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                  className="px-1 sm:px-2.5 py-0.5 sm:py-1 rounded text-[8px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-all cursor-pointer backdrop-blur-sm
                  bg-white/80 dark:bg-gray-800/80 
                  border border-gray-200 dark:border-gray-700 
                  text-gray-700 dark:text-gray-200 
                  hover:bg-white dark:hover:bg-gray-700 
                  w-[82px] sm:w-[132px]"
                >
                  {MONTH_OPTIONS.map((month) => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>

                <select
                  value={selectedYear}
                  onChange={e => setSelectedYear(e.target.value)}
                  className="px-1 sm:px-2.5 py-0.5 sm:py-1 rounded text-[8px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-all cursor-pointer backdrop-blur-sm
                  bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 w-[62px] sm:w-[86px]"
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>

                <button
                  onClick={resetFilters}
                  className="px-1 sm:px-2 py-0.5 sm:py-1 text-[8px] sm:text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded-md transition-all duration-200"
                >
                  Reset
                </button>
              </div>

              {/* Toolbar: stars count, show/hide filters, fullscreen */}
              <div className="ct-floating-toolbar">
                <span className="ct-toolbar-stat" title="Stars charted">
                  <Sparkles className="w-3 h-3 text-indigo-300" />
                  <span>{visibleCount > filtered.length ? filtered.length : visibleCount}/{filtered.length}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setShowFilters((v) => !v)}
                  className="ct-toolbar-btn"
                  title={showFilters ? 'Hide filters' : 'Show filters'}
                >
                  {showFilters ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="ct-toolbar-btn"
                  title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* ── Monthly Results ── */}
            <div className="monthly-results-panel mb-1 sm:mb-4 rounded-xl border border-white/8 bg-white/[0.035] p-1.5 sm:p-3">
              <div className="flex items-center gap-1.5 sm:gap-3 overflow-x-auto monthly-results-scroll pb-1">
                <button
                  onClick={() => setSelectedMonth('all')}
                  className={`month-result-card all-months flex-shrink-0 rounded-lg border px-2 sm:px-3 py-1.5 sm:py-2 text-left transition-all duration-300 ${selectedMonth === 'all'
                    ? 'border-cyan-300/50 bg-cyan-400/15 shadow-lg shadow-cyan-500/10'
                    : 'border-white/8 bg-white/[0.035] hover:bg-white/[0.08] hover:border-white/15'
                    }`}
                >
                  <span className="block text-[9px] sm:text-[11px] font-bold text-white">Year</span>
                  <span className="block text-[8px] sm:text-[10px] text-cyan-200/80">{yearEntries.length} entries</span>
                </button>
                {monthlyResults.map((month) => {
                  const isSelected = selectedMonth === month.value;
                  const hasActivity = month.entries > 0;
                  return (
                    <button
                      key={month.value}
                      onClick={() => setSelectedMonth(month.value)}
                      className={`month-result-card flex-shrink-0 rounded-lg border px-2 sm:px-3 py-1.5 sm:py-2 text-left transition-all duration-300 min-w-[72px] sm:min-w-[96px] ${isSelected
                        ? 'border-indigo-300/55 bg-indigo-400/15 shadow-lg shadow-indigo-500/10'
                        : hasActivity
                          ? 'border-white/10 bg-white/[0.05] hover:bg-white/[0.09] hover:border-white/18'
                          : 'border-white/6 bg-white/[0.025] opacity-60 hover:opacity-85'
                        }`}
                    >
                      <span className="flex items-center justify-between gap-1 text-[9px] sm:text-[11px] font-bold text-white">
                        {month.short}
                        {(month.running > 0 || month.ongoing > 0) && <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />}
                      </span>
                      <span className="block text-[10px] sm:text-xs font-semibold text-indigo-200 mt-0.5">{Math.round(month.hours * 10) / 10}h</span>
                      <span className="block text-[8px] sm:text-[10px] text-indigo-300/60">{month.entries} entries</span>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-4 gap-1 sm:gap-2 pt-1 sm:pt-2 text-center">
                <div className="rounded-lg bg-white/[0.04] px-1 py-1 sm:py-1.5">
                  <span className="block text-[8px] sm:text-[10px] text-indigo-300/60">Hours</span>
                  <span className="text-[10px] sm:text-sm font-bold text-white">{Math.round(periodStats.hours * 10) / 10}</span>
                </div>
                <div className="rounded-lg bg-white/[0.04] px-1 py-1 sm:py-1.5">
                  <span className="block text-[8px] sm:text-[10px] text-indigo-300/60">Entries</span>
                  <span className="text-[10px] sm:text-sm font-bold text-white">{periodStats.entries}</span>
                </div>
                <div className="rounded-lg bg-emerald-400/[0.08] px-1 py-1 sm:py-1.5">
                  <span className="block text-[8px] sm:text-[10px] text-emerald-300/70">Done</span>
                  <span className="text-[10px] sm:text-sm font-bold text-emerald-200">{periodStats.completed}</span>
                </div>
                <div className="rounded-lg bg-cyan-400/[0.08] px-1 py-1 sm:py-1.5">
                  <span className="block text-[8px] sm:text-[10px] text-cyan-300/70">Active</span>
                  <span className="text-[10px] sm:text-sm font-bold text-cyan-200">{periodStats.running + periodStats.ongoing}</span>
                </div>
              </div>
            </div>

            {/* ── View Template Picker ── */}
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-4">
              <span className="hidden sm:inline text-[10px] text-indigo-300/50 font-medium uppercase tracking-wider mr-1">View</span>
              {VIEW_TEMPLATES.map((t) => {
                const Icon = t.icon;
                const isActive = template === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTemplate(t.id)}
                    title={t.desc}
                    className={`template-btn group flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-3 py-0.5 sm:py-1.5 rounded-lg text-[9px] sm:text-[11px] font-medium transition-all duration-300 border ${isActive
                      ? 'bg-indigo-500/20 border-indigo-400/40 text-white shadow-lg shadow-indigo-500/10'
                      : 'bg-white/5 border-white/8 text-indigo-300/60 hover:text-white hover:bg-white/10 hover:border-white/15'
                      }`}
                  >
                    <Icon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 transition-transform duration-300 ${isActive ? 'text-indigo-400' : ''} group-hover:scale-110`} />
                    <span className="hidden sm:inline">{t.name}</span>
                  </button>
                );
              })}
            </div>

            {/* ── Day Navigation Cards ── */}
            {daySummaries.length > 0 && (
              <div className="day-nav-strip relative mb-1.5 sm:mb-5">
                {/* Scroll arrows */}
                {daySummaries.length > 4 && (
                  <>
                    <button
                      onClick={() => scrollDayCards(-1)}
                      className="day-nav-arrow left-0 absolute top-1/2 -translate-y-1/2 z-20 w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-white/10 border border-white/10 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all"
                    >
                      <ChevronLeft className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    </button>
                    <button
                      onClick={() => scrollDayCards(1)}
                      className="day-nav-arrow right-0 absolute top-1/2 -translate-y-1/2 z-20 w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-white/10 border border-white/10 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all"
                    >
                      <ChevronRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    </button>
                  </>
                )}

                <div
                  ref={dayCardsRef}
                  className="day-nav-scroll flex gap-1 sm:gap-2.5 overflow-x-auto pb-1 sm:pb-2 px-1 scrollbar-thin"
                  style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(99,102,241,0.3) transparent' }}
                >
                  {daySummaries.map((day, i) => {
                    const isToday = day.dateKey === new Date().toISOString().slice(0, 10);
                    const isActiveDayCard = activeDay === day.dateKey;
                    return (
                      <button
                        key={day.dateKey}
                        onClick={() => scrollToDay(day.dateKey)}
                        className={`day-card group flex-shrink-0 relative rounded-lg sm:rounded-xl border px-1.5 sm:px-4 py-1 sm:py-2.5 text-left transition-all duration-300 min-w-0 sm:min-w-[140px] ${isActiveDayCard
                          ? 'border-indigo-400/50 bg-indigo-500/15 shadow-lg shadow-indigo-500/10'
                          : 'border-white/8 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/15'
                          }`}
                        style={{ '--day-color': day.color }}
                      >
                        {/* Glow accent */}
                        <div
                          className="absolute top-0 left-0 w-full h-[1px] sm:h-[2px] rounded-t-lg sm:rounded-t-xl opacity-40 group-hover:opacity-70 transition-opacity"
                          style={{ background: `linear-gradient(90deg, ${day.color}, transparent)` }}
                        />

                        {/* Mobile: single line | Desktop: two lines */}
                        <div className="flex sm:flex-col gap-1 sm:gap-0">
                          <div className="flex items-center gap-1 sm:gap-1.5 sm:mb-1">
                            <Calendar className="w-2 h-2 sm:w-3 sm:h-3" style={{ color: day.color }} />
                            <span className="text-[9px] sm:text-xs font-bold text-white whitespace-nowrap">{fmtDate(day.dateKey)}</span>
                            {isToday && (
                              <span className="sm:hidden w-1 h-1 rounded-full bg-teal-400 flex-shrink-0" />
                            )}
                            {isToday && (
                              <span className="hidden sm:inline px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-teal-500/20 text-teal-400 border border-teal-500/30">
                                NOW
                              </span>
                            )}
                            {day.hasFinished && (
                              <span className="hidden sm:inline px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                COMPLETED
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-1 sm:gap-2 text-[8px] sm:text-[10px]">
                            <span className="text-indigo-300/60">
                              {day.count}<span className="hidden sm:inline"> {day.count === 1 ? 'entry' : 'entries'}</span>
                            </span>
                            <span className="font-semibold" style={{ color: day.color }}>
                              {Math.round(day.hours * 10) / 10}h
                            </span>
                            {(day.hasRunning || day.hasInProcess) && (
                              <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-teal-400 animate-pulse" />
                            )}
                          </div>
                        </div>

                        {/* Mini star dots preview - hidden on mobile */}
                        <div className="hidden sm:flex items-center gap-0.5 mt-1.5">
                          {Array.from({ length: Math.min(day.count, 8) }).map((_, j) => (
                            <div
                              key={j}
                              className="w-1.5 h-1.5 rounded-full opacity-50"
                              style={{ backgroundColor: colorAt(i * 3 + j) }}
                            />
                          ))}
                          {day.count > 8 && (
                            <span className="text-[8px] text-indigo-400/40 ml-0.5">+{day.count - 8}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

          </div>{/* end filters-collapsible */}

        </div>{/* end sticky-controls-bar */}

        {/* ── Empty state ── */}
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-indigo-300/60">
            <Sparkles className="w-12 h-12 mb-4 opacity-40" />
            <p className="text-lg font-semibold">No entries in this range</p>
            <p className="text-sm mt-1">Adjust the date filters to explore your timeline</p>
          </div>
        )}

        {/* ── Scatter canvas ── */}
        {filtered.length > 0 && (
          <div key={`${template}-${dateFrom}-${dateTo}-${filtered.length}`} className={`relative overflow-hidden constellation-canvas-building template-${template}`} style={{ height: totalHeight }} ref={canvasRef}>
            {/* Nebula fog patches */}
            {NEBULA_FOGS.map((fog, i) => (
              <div
                key={i}
                className="nebula-fog"
                style={{
                  top: fog.top,
                  left: fog.left,
                  width: fog.w,
                  height: fog.h,
                  background: fog.bg,
                  animationDelay: `${i * 3}s`,
                }}
              />
            ))}

            {/* SVG layer for curved constellation paths */}
            <svg
              className="absolute inset-0 w-full pointer-events-none"
              style={{ height: totalHeight }}
              viewBox={`0 0 100 ${totalHeight}`}
              preserveAspectRatio="none"
            >
              <defs>
                {renderedConnectors.map((c) => (
                  <linearGradient key={`g-${c.id}`} id={`grad-${c.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={c.colorA} stopOpacity="0.7" />
                    <stop offset="100%" stopColor={c.colorB} stopOpacity="0.7" />
                  </linearGradient>
                ))}
              </defs>

              {connectors.map((c, i) => {
                const isActive = activeId === c.aId || activeId === c.bId;
                const isFinishedSegment = c.isFinishedSegment;
                const isInProcessSegment = c.isInProcessSegment;
                return (
                  <path
                    key={c.id}
                    d={c.path}
                    className={`constellation-path ${isActive ? 'active' : ''} ${isFinishedSegment ? 'finished-segment' : ''} ${isInProcessSegment ? 'in-process-segment' : ''}`}
                    stroke={`url(#grad-${c.id})`}
                    strokeWidth={isActive ? '0.35' : '0.18'}
                    opacity={isFinishedSegment ? 0.5 : isInProcessSegment ? 0.46 : (isActive ? 0.85 : 0.30)}
                    pathLength="1"
                    strokeDasharray={isFinishedSegment ? '0.035 0.025' : '1'}
                    strokeDashoffset="1"
                    style={{ '--path-color': c.colorA, animationDelay: `${220 + i * 110}ms` }}
                  />
                );
              })}
            </svg>

            {/* Date headers */}
            {renderedGrouped.map(([dateKey]) => {
              const y = datePositions[dateKey];
              return (
                <div
                  key={`dh-${dateKey}`}
                  ref={el => (dayRefs.current[dateKey] = el)}
                  className="absolute left-1/2 -translate-x-1/2"
                  style={{ top: y }}
                >
                  <div className="ce-date-pill date-nebula">
                    <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: 'var(--ce-accent)' }} />
                    <span className="ce-date-label text-[10px] sm:text-xs">{fmtDate(dateKey)}</span>
                    <span className="ce-date-sub hidden sm:inline">{fmtFullDate(dateKey)}</span>
                    <span className="ce-date-coord hidden sm:inline">{starCoord(dateKey)}</span>
                  </div>
                </div>
              );
            })}

            {/* Star nodes + labels + cards */}
            {visibleFiltered.map((entry) => {
              const p = positions[entry.id];
              if (!p) return null;
              const isActive = activeId === entry.id;
              const isRunning = entry.isRunning;
              const isFinished = isFinishedEntry(entry);
              const isInProcess = !isRunning && !isFinished && isInProcessEntry(entry);
              const nodeColor = isRunning ? '#14b8a6' : (isFinished ? FINISHED_COLOR : (isInProcess ? IN_PROCESS_COLOR : p.color));

              // Per-template construction timing & motion vectors.
              // Use a "batch local" index so each progressive load
              // animates from delay 0 instead of carrying global offset.
              const rSeed = seedRandom(String(entry.id) + 'anim');
              const rSeed2 = seedRandom(String(entry.id) + 'twk');
              const localIdx = Math.max(0, p.globalIdx - prevVisibleRef.current);
              let animDelay;
              if (template === 'spiral') {
                animDelay = 140 + localIdx * 160; // slow, sequential galaxy formation
              } else if (template === 'scatter') {
                animDelay = Math.round(rSeed * 2400); // randomly across the field
              } else if (template === 'zigzag') {
                animDelay = localIdx * 120;
              } else {
                animDelay = localIdx * 80;
              }

              // Direction vector for slide/zigzag (left vs right)
              const sideSign = (p.x >= 50 ? 1 : -1);
              const zigzagDx = sideSign * (140 + (p.globalIdx % 3) * 40);
              const linearDy = -180 - (p.globalIdx % 5) * 30;
              const scatterDx = (rSeed - 0.5) * 320;
              const scatterDy = (rSeed2 - 0.5) * 220;
              const twinkleDelay = (rSeed2 * 4).toFixed(2);

              // Card placement: on mobile always position left-aligned below node to avoid clipping
              const cardOffset = isMobile ? 12 : 18;
              const cardSide = isMobile ? (p.x > 50 ? 'left' : 'right') : (p.x > 55 ? 'left' : 'right');
              const cardStyle = isMobile
                ? { left: '4%', right: '4%', top: p.y + 16, width: 'auto' }
                : cardSide === 'left'
                  ? { right: `calc(${100 - p.x}% + ${cardOffset}px)`, top: p.y - 10 }
                  : { left: `calc(${p.x}% + ${cardOffset}px)`, top: p.y - 10 };

              const nebulaName = nebulaPicks[entry.id];

              return (
                <React.Fragment key={entry.id}>
                  {/* Star node */}
                  <div
                    className={`star-node timeline-entry-animate ${isRunning ? 'running-pulse' : ''} ${isInProcess ? 'in-process-node' : ''} ${isFinished ? 'finished-node' : ''} ${isActive ? 'hovered' : ''}`}
                    style={{
                      '--node-color': nodeColor,
                      '--zigzag-dx': `${zigzagDx}px`,
                      '--linear-dy': `${linearDy}px`,
                      '--scatter-dx': `${scatterDx}px`,
                      '--scatter-dy': `${scatterDy}px`,
                      '--twinkle-delay': `${twinkleDelay}s`,
                      backgroundColor: nodeColor,
                      left: `calc(${p.x}% - ${isMobile ? 7 : 9}px)`,
                      top: p.y - (isMobile ? 7 : 9),
                      animationDelay: `${animDelay}ms`,
                    }}
                    onMouseEnter={() => setHoveredId(entry.id)}
                    onMouseLeave={() => { if (pinnedId !== entry.id) setHoveredId(null); }}
                    onClick={() => setPinnedId(pinnedId === entry.id ? null : entry.id)}
                  >
                    {/* Claude design: layered halo + ring + core */}
                    <span className="ce-node-halo" />
                    {(isRunning || isInProcess || isActive) && <span className="ce-node-ring" />}
                    <span className="ce-node-core" />
                    {isFinished && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border border-white/20 flex items-center justify-center z-10">
                        <CheckCircle className="w-2 h-2 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Nebula name overlay (only for 4 chosen entries) */}
                  {nebulaName && (
                    <div
                      className="nebula-name-label"
                      style={{
                        left: `calc(${p.x}% + 14px)`,
                        top: p.y - 26,
                        animationDelay: `${animDelay + 320}ms`,
                      }}
                    >
                      <span className="nebula-name-text">{nebulaName}</span>
                    </div>
                  )}

                  {/* Mini label beside node */}
                  <div
                    className="node-label"
                    style={{
                      left: cardSide === 'left' ? `calc(${p.x}% + 14px)` : undefined,
                      right: cardSide === 'right' ? `calc(${100 - p.x}% + 14px)` : undefined,
                      top: p.y - 5,
                      color: nodeColor,
                      fontSize: isMobile ? '9px' : '10px',
                    }}
                  >
                    {fmtTime(entry.startTime)}
                    {entry.isRunning && <span className="ml-1 text-teal-400">●</span>}
                    {isInProcess && <span className="ml-1 text-cyan-300">●</span>}
                    {isFinished && <span className="ml-1 text-emerald-400">✓</span>}
                  </div>

                  {/* Hover card (Claude design: accent border + top gradient) */}
                  <div
                    className={`timeline-card ce-detail-shell ${isActive ? 'visible' : ''}`}
                    style={{ ...cardStyle, '--accent': nodeColor }}
                    onMouseEnter={() => setHoveredId(entry.id)}
                    onMouseLeave={() => { if (pinnedId !== entry.id) setHoveredId(null); }}
                  >
                    <TimelineCard
                      entry={entry}
                      color={p.color}
                      elapsedMap={elapsedMap}
                      teamLead={teamLead}
                      onContinueEntry={onContinueEntry}
                    />
                  </div>
                </React.Fragment>
              );
            })}

            {/* Progressive-load sentinel + warp comet */}
            {hasMore && (
              <div
                ref={loadSentinelRef}
                className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                style={{ top: totalHeight - 80, height: 120 }}
              >
                <div className="warp-comet" aria-hidden="true">
                  <span className="warp-comet-head" />
                  <span className="warp-comet-trail" />
                </div>
                <span className="mt-3 text-[10px] sm:text-[11px] font-semibold tracking-widest uppercase text-indigo-200/80 px-3 py-1 rounded-full bg-black/40 border border-indigo-400/30 backdrop-blur-sm">
                  Charting next sector · {visibleCount}/{filtered.length}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
//  Timeline Card (compact hover card)
// ──────────────────────────────────────────────
const TimelineCard = ({ entry, color, elapsedMap, teamLead, onContinueEntry }) => {
  const isRunning = entry.isRunning;
  const isFinished = isFinishedEntry(entry);
  const isInProcess = !isRunning && !isFinished && isInProcessEntry(entry);
  const taskId = entry.referenceProductionTaskId || entry.productionTaskId;
  const canContinue = Boolean(onContinueEntry && taskId && !isRunning && !isFinished);

  const notesList = useMemo(() => {
    const raw = entry.notes || entry.description || '';
    if (!raw) return [];
    return raw.split('\n---\n').map((chunk) => {
      const match = chunk.match(/^\[(.+?)\]\s*([\s\S]*)$/);
      return match
        ? { timestamp: match[1], text: match[2].trim() }
        : { timestamp: null, text: chunk.trim() };
    }).filter(n => n.text);
  }, [entry.notes, entry.description]);

  return (
    <>
      <div className="timeline-card-glow" style={{ '--card-color': isFinished ? FINISHED_COLOR : (isInProcess ? IN_PROCESS_COLOR : color) }} />

      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-xl border border-white/20 dark:border-white/10 shadow-2xl p-2.5 sm:p-3.5 space-y-2 sm:space-y-2.5 w-full sm:w-[280px]">
        {/* Time & Duration + Status Badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-indigo-400" />
            <span className="text-xs font-semibold text-gray-900 dark:text-white">
              {fmtTime(entry.startTime)}
              {entry.endTime && !isRunning && (
                <span className="text-gray-400"> → {fmtTime(entry.endTime)}</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {isFinished && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-md">
                <CheckCircle className="w-2.5 h-2.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400">Finished</span>
              </div>
            )}
            <div
              className="px-2 py-0.5 rounded-md text-[11px] font-bold"
              style={{
                backgroundColor: `${isRunning ? '#14b8a6' : (isFinished ? FINISHED_COLOR : (isInProcess ? IN_PROCESS_COLOR : color))}18`,
                color: isRunning ? '#14b8a6' : (isFinished ? FINISHED_COLOR : (isInProcess ? IN_PROCESS_COLOR : color)),
              }}
            >
              {isRunning ? (elapsedMap[entry.id] || 'Running…') : fmtHours(entry)}
            </div>
          </div>
        </div>

        {/* Task / Description */}
        {(entry.referenceProductionTaskId || entry.description) && (
          <div className="flex items-start gap-1.5">
            <Layers className="w-3 h-3 text-purple-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                {entry.referenceProductionTaskId || entry.description}
              </p>
              {entry.referenceProductionTaskId && entry.description && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {entry.description}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Activity + Version inline */}
        <div className="flex items-center gap-2 flex-wrap">
          {(entry.activityName || entry.activityId) && (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-md">
              <Layers className="w-2.5 h-2.5 text-indigo-500" />
              <span className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400">
                {entry.activityName || entry.activityId}
              </span>
            </div>
          )}
          {entry.version > 1 && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400">
              v{entry.version}
            </span>
          )}
          {entry.processingStatusCode && !isFinished && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${entry.processingStatusCode === "In Process"
              ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              }`}>
              {entry.processingStatusCode}
            </span>
          )}
        </div>

        {/* Notes */}
        {notesList.length > 0 && (
          <div className="border-t border-gray-100 dark:border-gray-700/50 pt-2">
            <div className="flex items-center gap-1 mb-1">
              <StickyNote className="w-2.5 h-2.5 text-amber-500" />
              <span className="text-[9px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                Notes ({notesList.length})
              </span>
            </div>
            {notesList.slice(0, 2).map((note, i) => (
              <p key={i} className="text-[10px] text-gray-600 dark:text-gray-400 truncate leading-relaxed">
                {note.timestamp && (
                  <span className="text-[9px] text-gray-400 dark:text-gray-500 font-mono mr-1">[{note.timestamp}]</span>
                )}
                {note.text}
              </p>
            ))}
            {notesList.length > 2 && (
              <p className="text-[9px] text-gray-400 mt-0.5">+{notesList.length - 2} more</p>
            )}
          </div>
        )}

        {/* Team Lead */}
        {teamLead && (
          <div className="border-t border-gray-100 dark:border-gray-700/50 pt-2">
            <div className="flex items-center gap-2 p-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800/30">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                <User className="w-3 h-3 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-gray-800 dark:text-gray-200 truncate">{teamLead.name}</p>
                <p className="text-[9px] text-gray-500 dark:text-gray-400 truncate">
                  Team Lead{teamLead.teamName ? ` · ${teamLead.teamName}` : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {canContinue && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onContinueEntry(entry);
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-white text-[11px] font-bold shadow-lg shadow-cyan-500/20 transition-all"
          >
            <Play className="w-3 h-3 fill-current" /> Continue
          </button>
        )}

        {/* Sync status */}
        <div className="flex items-center justify-between text-[9px] text-gray-400 dark:text-gray-500 pt-0.5">
          <span>{entry.employeeName}</span>
          <span className={`px-1.5 py-0.5 rounded ${entry.syncStatus === 'Synced' ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' :
            entry.syncStatus === 'Failed' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
              'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            }`}>
            {entry.syncStatus || 'Pending'}
          </span>
        </div>
      </div>
    </>
  );
};

export default ConstellationTimeline;